import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Loader2, FileSpreadsheet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { sanitizeField } from "@/lib/validation";
import { useAuditLog } from "@/hooks/useAuditLog";

const FIELD_MAP: Record<string, string> = {
  org_name: "Organization",
  contact_name: "Contact",
  contact_email: "Email",
  phone: "Phone",
  zip_code: "ZIP",
  source_type: "Source Type",
  title: "Title",
  address: "Address",
  notes: "Notes",
};
const FIELDS = Object.keys(FIELD_MAP);

// Consistent with other bulk-data caps in the app (e.g. usePaginatedLeads'
// 10k CSV export cap) — sized for a manual partner-directory import, well
// above realistic use, while keeping the browser parse + single insert() fast.
const MAX_FILE_SIZE_MB = 5;
const MAX_IMPORT_ROWS = 2000;

function normalizeHeader(h: string): string | null {
  const l = h.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (l.includes("org") || l.includes("company") || l.includes("organization")) return "org_name";
  if (l.includes("email") || l.includes("mail")) return "contact_email";
  if (l.includes("contact") || l.includes("name")) return "contact_name";
  if (l.includes("phone") || l.includes("tel")) return "phone";
  if (l.includes("zip") || l.includes("postal")) return "zip_code";
  if (l.includes("source") || l.includes("type")) return "source_type";
  if (l.includes("title") || l.includes("role")) return "title";
  if (l.includes("address") || l.includes("street")) return "address";
  if (l.includes("note")) return "notes";
  return null;
}

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map(line => {
    const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = vals[i] || ""; });
    return row;
  });
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onImported: () => void;
}

export default function ReferralImportModal({ open, onOpenChange, onImported }: Props) {
  const { user } = useAuth();
  const { logAction } = useAuditLog();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [colMap, setColMap] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [dragging, setDragging] = useState(false);

  const processFile = useCallback(async (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["csv", "xlsx", "xls"].includes(ext)) {
      toast.error("Unsupported file type. Please upload a .csv, .xlsx, or .xls file.");
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }
    let parsed: Record<string, string>[] = [];
    if (ext === "csv") {
      const text = await file.text();
      parsed = parseCSV(text);
    } else {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      parsed = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: "" });
    }
    if (!parsed.length) { toast.error("No data found"); return; }
    if (parsed.length > MAX_IMPORT_ROWS) {
      toast.error(`File contains ${parsed.length.toLocaleString()} rows. The maximum is ${MAX_IMPORT_ROWS.toLocaleString()} rows per import — please split it into smaller files.`);
      return;
    }
    const hdrs = Object.keys(parsed[0]);
    setHeaders(hdrs);
    setRows(parsed);
    const auto: Record<string, string> = {};
    hdrs.forEach(h => { const m = normalizeHeader(h); if (m) auto[h] = m; });
    setColMap(auto);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  async function handleImport() {
    if (!user) return;
    setImporting(true);
    const records = rows.map(row => {
      const r: Record<string, any> = { user_id: user.id, org_name: "Unknown" };
      Object.entries(colMap).forEach(([srcCol, field]) => {
        if (row[srcCol]) r[field] = sanitizeField(row[srcCol], field === "notes" ? 2000 : field === "contact_email" ? 255 : 500);
      });
      return r;
    }).filter(r => r.org_name && r.org_name !== "Unknown");

    if (!records.length) { toast.error("No valid rows with organization name"); setImporting(false); return; }
    const { error } = await supabase.from("referral_leads").insert(records as any);
    setImporting(false);
    if (error) { toast.error("Import failed: " + error.message); return; }
    logAction("import", "referral_lead", undefined, { count: records.length });
    toast.success(`${records.length} referrals imported`);
    setRows([]); setHeaders([]); setColMap({});
    onImported();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import Referrals</DialogTitle></DialogHeader>

        {rows.length === 0 ? (
          <div
            className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-12 transition-colors cursor-pointer ${dragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"}`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <FileSpreadsheet className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Drag & drop a CSV or Excel file here, or click to browse</p>
            <p className="text-xs text-muted-foreground">Supports .csv, .xlsx, .xls — up to {MAX_FILE_SIZE_MB}MB, {MAX_IMPORT_ROWS.toLocaleString()} rows</p>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) processFile(f); }} />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{rows.length} rows found. Map columns below:</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {headers.map(h => (
                <div key={h} className="flex items-center gap-2">
                  <span className="truncate font-medium w-32">{h}</span>
                  <span className="text-muted-foreground">→</span>
                  <select
                    className="flex-1 rounded border px-2 py-1 text-sm bg-background"
                    value={colMap[h] || ""}
                    onChange={e => setColMap(prev => ({ ...prev, [h]: e.target.value }))}
                  >
                    <option value="">Skip</option>
                    {FIELDS.map(f => <option key={f} value={f}>{FIELD_MAP[f]}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="rounded-lg border overflow-x-auto max-h-48">
              <Table>
                <TableHeader>
                  <TableRow>{headers.slice(0, 5).map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}</TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 5).map((row, i) => (
                    <TableRow key={i}>{headers.slice(0, 5).map(h => <TableCell key={h} className="text-xs">{row[h]}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setRows([]); setHeaders([]); setColMap({}); }}>Clear</Button>
              <Button onClick={handleImport} disabled={importing || !Object.values(colMap).includes("org_name")}>
                {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</> : <><Upload className="h-4 w-4 mr-2" /> Import {rows.length} Rows</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
